import { apiRequest } from "@/lib/http";
import { authStorage, type StoredTokens } from "@/lib/auth-storage";

export type AgreementStatus = "PENDING" | "SIGNED" | "NOT_REQUIRED";

export interface ApiUser {
  id: string;
  email: string;
  role: "ADMIN" | "DOZENT";
  status: "INVITED" | "ACTIVE" | "BLOCKED";
  agreementStatus?: AgreementStatus;
  agreementSignedAt?: string | null;
  firstName: string | null;
  lastName: string | null;
  hasFullCatalogAccess: boolean;
  invitedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  user: ApiUser;
  tokens: StoredTokens;
}

export type SaasLoginResult =
  | ({ status: "AUTHENTICATED" } & AuthResponse)
  | ({
      status: "MFA_GRACE_AUTHENTICATED";
      mfaPolicy: {
        required: true;
        enrolled: false;
        graceStartedAt: string;
        graceExpiresAt: string;
      };
    } & AuthResponse)
  | {
      status: "MFA_REQUIRED" | "MFA_SETUP_REQUIRED";
      challenge: {
        token: string;
        expiresAt: string;
      };
      /** Methods the account can use (passkeys release, additive). */
      methods?: Array<"TOTP" | "RECOVERY_CODE" | "PASSKEY">;
    };

export type AcceptInviteResponse =
  | {
      status: "AGREEMENT_REQUIRED";
      agreementToken: string;
      user: ApiUser;
    }
  | ({
      status: "COMPLETED";
    } & AuthResponse);

export type InvitePhase = "PASSWORD" | "AGREEMENT" | "COMPLETED";

export function login(email: string, password: string, captchaToken?: string) {
  return loginDozent(email, password, captchaToken);
}

export function loginDozent(
  email: string,
  password: string,
  captchaToken?: string,
) {
  return apiRequest<SaasLoginResult>("/auth/dozent/login", {
    method: "POST",
    body: JSON.stringify({ email, password, captchaToken }),
    skipAuth: true,
  });
}

export function loginAdmin(
  email: string,
  password: string,
  captchaToken?: string,
) {
  return apiRequest<SaasLoginResult>("/auth/admin/login", {
    method: "POST",
    body: JSON.stringify({ email, password, captchaToken }),
    skipAuth: true,
  });
}

export function acceptInvite(input: {
  token: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}) {
  return apiRequest<AcceptInviteResponse>("/auth/accept-invite", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuth: true,
  });
}

export function getInviteStatus(token: string, email: string) {
  return apiRequest<{
    phase: InvitePhase;
    agreementToken?: string;
    user?: ApiUser;
  }>("/auth/invite/status", {
    method: "POST",
    body: JSON.stringify({ token, email }),
    skipAuth: true,
  });
}

export function forgotPassword(email: string) {
  return apiRequest<{ success: boolean }>("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
}

export function resetPassword(input: {
  email: string;
  token: string;
  password: string;
}) {
  return apiRequest<{ success: boolean }>("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify(input),
    skipAuth: true,
  });
}

export function getCurrentUser() {
  return apiRequest<ApiUser>("/auth/me");
}

export function logout() {
  return apiRequest<{ success: boolean }>("/auth/logout", {
    method: "POST",
  });
}

export function inviteUser(email: string, role: "ADMIN" | "DOZENT") {
  return apiRequest<{
    token: string;
    invitation: { id: string; email: string; role: string; expiresAt: string };
  }>("/auth/invite", {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
}

export function saveTokens(tokens: StoredTokens) {
  authStorage.save(tokens);
}

export function clearTokens() {
  authStorage.clear();
}
